import { useMemo } from "react";
import { Package, AlertTriangle, DollarSign, Tag } from "lucide-react";
import { Card } from "@/components/ui/card";

const MetricsCards = ({ stats, filteredProducts = [], tipoCambio = 1, userRole = "admin" }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) + ' Bs';
  };

  const totalPMayor = useMemo(
    () => filteredProducts.reduce((sum, p) => sum + ((p.pmayor || 0) * (p.stock || 0)), 0),
    [filteredProducts]
  );

  const totalUSD = useMemo(
    () => filteredProducts.reduce((sum, p) => sum + ((p.precio_usd || 0) * (p.stock || 0)), 0),
    [filteredProducts]
  );

  const formatUSD = (val) => '$ ' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);

  const allMetrics = [
    {
      title: "Total de Productos",
      value: stats.total_items,
      icon: Package,
      accent: "border-l-[#232F3E] dark:border-l-[#FF9900]",
      iconBg: "bg-[#232F3E]/10 dark:bg-[#FF9900]/10",
      iconColor: "text-[#232F3E] dark:text-[#FF9900]",
      testId: "metric-total-items",
      monetary: false,
    },
    {
      title: "Alertas Stock Bajo",
      value: stats.low_stock_count,
      icon: AlertTriangle,
      accent: "border-l-amber-500",
      iconBg: "bg-amber-50 dark:bg-amber-900/20",
      iconColor: "text-amber-600 dark:text-amber-400",
      testId: "metric-low-stock",
      monetary: false,
    },
    {
      title: "Bajo Minimo Requerido",
      value: stats.alerta_stock_minimo_count ?? 0,
      icon: AlertTriangle,
      accent: "border-l-orange-500",
      iconBg: "bg-orange-50 dark:bg-orange-900/20",
      iconColor: "text-orange-600 dark:text-orange-400",
      testId: "metric-stock-minimo",
      monetary: false,
    },
    {
      title: "Sin Stock",
      value: stats.out_of_stock_count,
      icon: AlertTriangle,
      accent: "border-l-red-500",
      iconBg: "bg-red-50 dark:bg-red-900/20",
      iconColor: "text-red-600 dark:text-red-400",
      testId: "metric-out-of-stock",
      monetary: false,
    },
    {
      title: "Valor Total Inventario",
      value: formatCurrency(stats.total_value),
      usd: formatUSD(totalUSD),
      icon: DollarSign,
      accent: "border-l-emerald-500",
      iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
      iconColor: "text-emerald-600 dark:text-emerald-400",
      testId: "metric-total-value",
      monetary: true,
    },
    {
      title: `Total PMayor (${filteredProducts.length})`,
      value: formatCurrency(totalPMayor),
      usd: formatUSD(totalUSD),
      icon: Tag,
      accent: "border-l-[#FF9900]",
      iconBg: "bg-[#FF9900]/10",
      iconColor: "text-[#FF9900]",
      testId: "metric-total-pmayor",
      monetary: true,
    }
  ];

  const metrics = userRole === "admin" ? allMetrics : allMetrics.filter(m => !m.monetary);

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 ${metrics.length > 4 ? 'lg:grid-cols-6' : metrics.length > 3 ? 'lg:grid-cols-5' : ''} gap-4`}>
      {metrics.map((metric) => {
        const Icon = metric.icon;
        return (
          <Card
            key={metric.testId}
            data-testid={metric.testId}
            className={`p-4 metric-card bg-card border-l-4 ${metric.accent} border border-border shadow-sm`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="body-font text-xs text-muted-foreground mb-1 uppercase tracking-wide">{metric.title}</p>
                <p className="mono-font text-xl font-bold text-foreground truncate">{metric.value}</p>
                {metric.usd && (
                  <p className="mono-font text-sm text-[#FF9900] font-semibold mt-0.5" data-testid={`${metric.testId}-usd`}>
                    {metric.usd}
                  </p>
                )}
              </div>
              <div className={`${metric.iconBg} p-2.5 rounded-lg shrink-0`}>
                <Icon className={`w-5 h-5 ${metric.iconColor}`} />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default MetricsCards;
